<template>
  <c-box data-cy="product-list">
    <c-box :m="{ base: '0 -31px', md: '0 0' }">
      <c-flex
        v-for="p in searchResult.value"
        :key="p.sku"
        :p="{
          base: 'var(--chakra-space-lg) var(--chakra-space-md) var(--chakra-space-lg) var(--chakra-space-sm)',
          md: 'var(--chakra-space-2xl) 32px calc(var(--chakra-space-2xl) - var(--chakra-space-lg)) 18px',
          xl: 'var(--chakra-space-2xl) 32px calc(var(--chakra-space-2xl) - var(--chakra-space-lg)) 18px',
          '2xl':
            'var(--chakra-space-2xl) 46px calc(var(--chakra-space-2xl) - var(--chakra-space-lg)) 25px',
        }"
        alignItems="start"
        :mb="{ base: '2px', md: '20px' }"
        bg="white"
        :rounded="{ base: 0, md: 'base' }"
        v-chakra="{
          label: {
            color: 'gray.700',
            fontWeight: '400',
            fontSize: 'md',
            lineHeight: '26px',
            mb: 'xs',
            d: { base: 'none', md: 'block' },
          },
        }"
      >
        <c-box
          alignSelf="start"
          w="20%"
          minWidth="20%"
          :mr="{ base: '10px', md: '20px' }"
          :mb="{ base: '0px', md: 'lg' }"
          cursor="pointer"
          @click="displayProduct(p.sku)"
        >
          <img
            :src="p.main_image"
            :alt="`${$t('common.imageOfProduct')} ${p.sku} ${p.product_name}`"
            v-chakra
            w="auto"
            h="auto"
            maxWidth="100%"
          />
        </c-box>
        <c-box w="75%" :mt="{ base: '4px', md: 0 }">
          <c-box
            d="inline-block"
            verticalAlign="top"
            :w="{
              base: '67%',
              // title md+ width explained:
              // - When parent wrapper (containing title and sku+price) width is
              //   - less than 400px, use 100%
              //   - between 400px and 640px, use 100% - 160px
              //   - more than 640px, use 100% - 320px
              //      100% - 160px - [    160px, if 100% of parent wrapper >= 640px  ] + [           160px, if 100% of parent wrapper < 400px           ]
              md: 'calc(100% - 160px - clamp(0px, 100% - 640px, 1px / 1000) * 160 * 1000 + (1px / 1000 - clamp(0px, 100% - 400px, 1px / 1000)) * 160 * 1000)',
            }"
            :mb="{ base: '0px', md: 'lg' }"
            :pr="{ base: '10px', md: '20px' }"
            cursor="pointer"
            @click="displayProduct(p.sku)"
            tabindex="0"
            @keydown.enter="displayProduct(p.sku)"
          >
            <c-heading
              as="h3"
              :fontSize="{ base: 'sm', md: '22px' }"
              fontWeight="400"
              color="brand.purple"
              d="block"
              lineHeight="1.2"
              :mb="{ base: '2xs', md: 'sm' }"
              >{{ p.product_name }}</c-heading
            >
            <c-text
              :d="{ base: 'block', md: 'none' }"
              :fontSize="{ base: '10px', md: '16px' }"
              fontWeight="400"
              color="brand.black"
              mb="xs"
              >{{ p.sku }}</c-text
            >
            <c-text
              :d="{ base: 'none', md: 'block' }"
              v-if="p.subtitle"
              fontSize="md"
              fontWeight="400"
              color="gray.700"
              mb="md"
              >{{ p.subtitle }}</c-text
            >
            <c-text
              d="block"
              v-if="p.additional_note"
              fontStyle="italic"
              :fontSize="{ base: '10px', md: 'md' }"
              color="brand.red"
              :mb="{ base: 'md', md: 'md' }"
              >{{ p.additional_note }}</c-text
            >
            <c-text
              :d="{ base: 'none', md: 'block' }"
              v-if="p.short_description"
              fontSize="sm"
              fontWeight="400"
              color="brand.black"
              >{{ p.short_description }}</c-text
            >
          </c-box>
          <c-box
            d="inline-block"
            verticalAlign="top"
            :textAlign="{ base: 'right', md: 'left' }"
            :w="{
              base: '33%',
              // sku+price wrapper md+ width explained:
              // - When parent wrapper (containing title and sku+price) width is between 400px and 640px, width of this is 160px, otherwise 320px.
              // - Width of children is 160px each (sku & price), so when the wrapper width is 320px, they render '--', otherwise '='
              //      160px + [    160px, if 100% of parent wrapper >= 640px  ] + [           160px, if 100% of parent wrapper < 400px           ]
              md: 'calc(160px + clamp(0px, 100% - 640px, 1px / 1000) * 160 * 1000 + (1px / 1000 - clamp(0px, 100% - 400px, 1px / 1000)) * 160 * 1000)',
            }"
            :mb="{ base: '0px', md: 'lg' }"
          >
            <c-box
              :d="{ base: 'none', md: 'inline-block' }"
              verticalAlign="top"
              minWidth="50%"
              :pr="{ base: '10px', md: '20px' }"
              mb="20px"
              cursor="pointer"
              @click="displayProduct(p.sku)"
            >
              <c-text as="label">
                {{ $t("productSearch.productList.productId") }}
              </c-text>
              <c-text>
                <c-text
                  as="span"
                  fontSize="20px"
                  lineHeight="1.0"
                  v-html="addBreakingHintsToSku(p.sku)"
                ></c-text>
              </c-text>
            </c-box>
            <c-box
              v-if="isOrderingAllowedForUser()"
              d="inline-block"
              verticalAlign="top"
              :pr="{ base: '12px', md: '20px' }"
              :mb="{ base: '-16px', md: 0 }"
              whiteSpace="nowrap"
              overflow="visible"
              text-overflow="ellipsis"
            >
              <c-text as="label">
                {{ $t("productSearch.productList.unitPrice") }}
              </c-text>
              <Price
                :price="Number(p.price)"
                :campaign-price="Number(p.partnerDiscountPrice)"
                :currency="p.currencyCode"
                whiteSpace="nowrap"
                lineHeight="1.0"
                :textAlign="{ base: 'right', md: 'left' }"
                v-chakra="{
                  '.price__value': {
                    fontSize: { base: 'sm', md: 'xl' },
                    fontWeight: '400',
                    mb: 'md',
                  },
                  '.price__value--old': {
                    fontSize: { base: '10px', md: 'md' },
                    color: 'brand.grayDark',
                    d: 'inline-block',
                    mt: 'xs',
                  },
                }"
              />
            </c-box>
          </c-box>
          <c-box d="block">
            <c-text
              :d="{ base: 'block', md: 'none' }"
              v-if="p.subtitle"
              fontSize="10px"
              fontWeight="400"
              color="gray.700"
              >{{ p.subtitle }}</c-text
            >
          </c-box>
        </c-box>
        <c-box
          v-if="isOrderingAllowedForUser()"
          alignSelf="start"
          justifySelf="end"
          :w="{ base: '88px', md: '110px' }"
          :minWidth="{ base: '88px', md: '110px' }"
          @click.stop
        >
          <label></label>
          <c-flex mr="0" ml="auto">
            <c-input
              :ref="`qty-${p.sku}`"
              :value="p.quantity || 1"
              autocomplete="off"
              min="1"
              max="2000"
              type="number"
              width="auto"
              class="number"
              px="0"
              :w="{ base: '44px', md: '55px' }"
              :h="{ base: '32px', md: '40px' }"
              roundedLeft="base"
              roundedRight="0"
              borderRight="0"
              :borderColor="getQtyBorderColor(p)"
              :color="isOrderingAllowed(p.family) ? 'gray.600' : 'brand.grayDark'"
              :_hover="{ borderColor: getQtyBorderColor(p) }"
              :_active="{ borderColor: getQtyBorderColor(p) }"
              :_focus="{ borderColor: getQtyBorderColor(p) }"
              textAlign="center"
              :data-cy="`productList.countInput_${p.sku}`"
              @change="changeQuantity(p, $event)"
              @blur="onQuantityBlur(p)"
            />
            <c-button
              :class="isOrderingAllowed(p.family) ? '' : 'restricted'"
              :w="{ base: '44px', md: '55px' }"
              p="0"
              :h="{ base: '32px', md: '40px' }"
              :background="getOrderButtonColor(p)"
              :cursor="isOrderingAllowed(p.family) ? 'pointer' : 'not-allowed'"
              :_hover="{
                bg: getOrderButtonColor(p),
              }"
              :_active="{ bg: getOrderButtonColor(p) }"
              :borderColor="getOrderButtonColor(p)"
              :data-cy="`productList.addButton_${p.sku}`"
              :aria-label="$t('productSearch.productList.buttonLabel')"
              roundedLeft="0"
              roundedRight="base"
              @click="addToCart(p)"
            >
              <c-icon name="cart" color="white" size="1rem" />
            </c-button>
          </c-flex>
          <c-box>
            <c-text
              as="p"
              v-if="quantityOverProductSpecificMaxLimit[p.sku]"
              color="brand.red"
              :fontSize="{ base: 'xs', md: 'md' }"
              mt="10px"
            >
              {{ $t("shoppingCart.maxOrderQuantity.title") + " " + p.maximum_order_quantity }}
            </c-text>
          </c-box>
          <c-box
            v-chakra="{
              '.addProductTransition-enter-active': {
                transition: 'opacity 0.25s',
              },
              '.addProductTransition-leave-active': {
                transition: 'opacity 3s',
              },
              '.addProductTransition-enter, .addProductTransition-leave-to': {
                opacity: '0',
              },
            }"
          >
            <transition name="addProductTransition">
              <c-box v-if="showAdded[p.sku]" mt="5px" color="brand.purple" textAlign="right">
                {{ $t("productSearch.productList.addedToCart") }}
              </c-box>
            </transition>
          </c-box>
        </c-box>
      </c-flex>
    </c-box>
  </c-box>
</template>

<script lang="ts">
import Price from "@/design-system/elements/Price.vue";
import { IProductSearchState } from "@/store/productSearchModule";
import { CBox, CButton, CFlex, CHeading, CIcon, CInput, CText } from "@chakra-ui/vue";
import {
  CorePermission,
  IOrderRow,
  IPartnerProductSearchResultValue,
  IRootState,
} from "@iloq/shared";
import Vue, { defineComponent } from "vue";
import { mapState } from "vuex";
import { isQuantityOverProductSpecificMaxLimit, productToOrderRow } from "../../util/ProductUtil";

export default defineComponent({
  name: "ProductList",
  components: {
    Price,
    CIcon,
    CBox,
    CFlex,
    CText,
    CHeading,
    CInput,
    CButton,
  },
  computed: {
    ...mapState({
      user: (state: unknown) => (state as IRootState).user!,
    }),
    ...mapState("productSearch", {
      searchResult: (state: unknown) => (state as IProductSearchState).searchResult,
    }),
  },
  data() {
    return {
      showAdded: {} as { [key: string]: boolean },
      quantityOverProductSpecificMaxLimit: {} as { [key: string]: boolean },
    };
  },
  methods: {
    isOrderingAllowed(family: string | null | undefined): boolean {
      return this.$store.getters["orderingPermission/isOrderingAllowed"](family);
    },
    isOrderingAllowedForUser(): boolean {
      return (this as any).user.hasPermissions([CorePermission.order]);
    },
    addBreakingHintsToSku(input: string): string {
      return input.replace(/\./g, ".<wbr>");
    },
    displayProduct(sku: string): void {
      this.$router.push({ path: `/products/${sku}` });
    },
    changeQuantity(p: any, event: any): void {
      const quantity = parseInt(event.target.value, 10);
      if (quantity > 0) {
        p.quantity = quantity;
        (this.$refs[`qty-${p.sku}`] as any)[0].$el.value = p.quantity.toString();
        if (
          isQuantityOverProductSpecificMaxLimit(
            this.$store.state.shoppingCart.cartRows,
            quantity,
            p,
          )
        ) {
          Vue.set(this.quantityOverProductSpecificMaxLimit, p.sku, true);
        } else {
          Vue.set(this.quantityOverProductSpecificMaxLimit, p.sku, false);
        }
      } else {
        p.quantity = event.target.value;
      }
    },
    getQtyBorderColor(p: any): string {
      if (!this.isOrderingAllowed(p.family)) {
        return "brand.grayDark";
      }
      if (this.quantityOverProductSpecificMaxLimit[p.sku]) {
        return "brand.red";
      }
      return "brand.purple";
    },
    getOrderButtonColor(p: any): string {
      if (!this.isOrderingAllowed(p.family)) {
        return "brand.grayDark";
      }
      return "brand.purple";
    },
    onQuantityBlur(p: any): void {
      if (!(p.quantity > 0)) {
        p.quantity = 1;
        (this.$refs[`qty-${p.sku}`] as any)[0].$el.value = p.quantity.toString();
      }
    },
    addToCart(p: IPartnerProductSearchResultValue): void {
      const quantity = (p as any).quantity || 1;
      const orderRow: IOrderRow = productToOrderRow(p, parseInt(`${quantity}`, 10));

      if (
        isQuantityOverProductSpecificMaxLimit(this.$store.state.shoppingCart.cartRows, quantity, p)
      ) {
        Vue.set(this.quantityOverProductSpecificMaxLimit, p.sku, true);
      } else {
        this.$store.dispatch("shoppingCart/addToCart", orderRow).then((added) => {
          // Note: shoppingCart/addToCart action notifies the user if the product could not be added
          if (added) {
            Vue.set(this.showAdded, p.sku, true);
            setTimeout(() => {
              Vue.set(this.showAdded, p.sku, false);
            }, 500);
          }
          // if cannot be added, show error
        });
      }
    },
  },
});
</script>
